import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { ColumnDef, ColumnSizingState, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CloseButton, Popover, PopoverButton, PopoverPanel, useClose } from '@headlessui/react';
import { useWorkLogs, useDeleteWorkLog, useUpdateWorkLog, useCreateWorkLog } from 'service/project/hooks/useWorkLog';
import { getInitials } from 'utils/getInitials';
import { formatDate } from 'utils/helpers';
import LogTimeModal, { LogTimeData } from 'components/Modals/LogTimeModal';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';
import { Copy, List, SquarePen, Table, Trash2, X } from 'lucide-react';
import { usePmoNavigation } from 'hooks/usePmoNavigation';
import { toast } from 'react-hot-toast';
import { formatTimeSpent } from 'utils/timeTracking';

type WorkLogView = 'list' | 'table';

const VIEW_STORAGE_KEY = 'worklap.workLogView';
const COLUMN_SIZING_STORAGE_KEY = 'worklap.workLogColumnSizing';

// Only sizing is driven by the table instance; cells are rendered by hand below
const TABLE_COLUMNS: ColumnDef<any>[] = [
  { id: 'loggedBy', header: 'Logged by', size: 180, minSize: 130 },
  { id: 'workDate', header: 'Work date', size: 120, minSize: 100 },
  { id: 'timeSpent', header: 'Time spent', size: 110, minSize: 95 },
  { id: 'description', header: 'Description', size: 300, minSize: 160, maxSize: 1200 },
  { id: 'loggedOn', header: 'Logged on', size: 200, minSize: 140 },
  // Not resizable: it absorbs any spare width so the other columns render at exactly their dragged size
  { id: 'action', header: 'Action', size: 100, minSize: 96, enableResizing: false },
];

const RESIZABLE_COLUMN_IDS = new Set(
  TABLE_COLUMNS.filter((column) => column.enableResizing !== false).map((column) => column.id as string),
);

const readStoredView = (): WorkLogView => {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'list';
  } catch {
    return 'list';
  }
};

const readStoredColumnSizing = (): ColumnSizingState => {
  try {
    const parsed = JSON.parse(localStorage.getItem(COLUMN_SIZING_STORAGE_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    // Drop widths for columns that no longer exist or can't be resized, and anything malformed
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([id, width]) => RESIZABLE_COLUMN_IDS.has(id) && typeof width === 'number' && Number.isFinite(width) && width > 0,
      ),
    ) as ColumnSizingState;
  } catch {
    return {};
  }
};

const POPOVER_WIDTH = 520;
const POPOVER_MIN_WIDTH = 280;

// Mounted only while the popover is open. The panel is portaled to <body>, so without this it would
// keep following its button when the task column or table scrolls and drift over the task header.
const CloseOnOutsideScroll: React.FC<{ panelRef: React.RefObject<HTMLElement | null> }> = ({ panelRef }) => {
  const close = useClose();

  useEffect(() => {
    const onScroll = (e: Event) => {
      // Scrolling the description inside the panel is fine
      if (panelRef.current?.contains(e.target as Node)) return;
      close();
    };
    // Capture phase, since scroll events don't bubble from inner containers
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [close, panelRef]);

  return null;
};

interface DescriptionCellProps {
  log: any;
  boundaryRef: React.RefObject<HTMLElement | null>;
}

// Clamps to two lines at the column's current width, so widening the column reveals more text.
// The full text opens in a popover so rows keep their height.
const DescriptionCell: React.FC<DescriptionCellProps> = ({ log, boundaryRef }) => {
  const description: string = log.description;
  const textRef = useRef<HTMLParagraphElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [placement, setPlacement] = useState<{ anchor: 'bottom start' | 'bottom end'; width: number }>({
    anchor: 'bottom start',
    width: POPOVER_WIDTH,
  });

  // Keeps the panel inside the work log area so it doesn't cover the task sidebar:
  // open rightwards when there's room, otherwise leftwards, and narrow it to fit
  const updatePlacement = (button: HTMLElement) => {
    const bounds = boundaryRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const rect = button.getBoundingClientRect();
    const spaceRight = bounds.right - rect.left;
    const spaceLeft = rect.right - bounds.left;
    const alignEnd = spaceRight < POPOVER_WIDTH && spaceLeft > spaceRight;
    setPlacement({
      anchor: alignEnd ? 'bottom end' : 'bottom start',
      width: Math.max(POPOVER_MIN_WIDTH, Math.min(POPOVER_WIDTH, alignEnd ? spaceLeft : spaceRight)),
    });
  };

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => setIsClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [description]);

  return (
    <>
      <p ref={textRef} className="whitespace-pre-wrap break-words line-clamp-2">
        {description}
      </p>
      {isClamped && (
        <Popover>
          <PopoverButton
            onClick={(e) => updatePlacement(e.currentTarget)}
            className="rounded-sm text-xs text-[#7C6CF7] hover:text-purple-300 transition-colors"
          >
            view full
          </PopoverButton>
          {/* Floating UI owns max-width (viewport fit), so the boundary fit is applied through width */}
          <PopoverPanel
            ref={panelRef}
            transition
            anchor={placement.anchor}
            style={{ width: placement.width }}
            className="z-[100] [--anchor-gap:8px] [--anchor-padding:16px] flex flex-col rounded-xl border border-primary-subtle bg-primary-intemediate shadow-xl focus:outline-none transition duration-100 ease-out data-[closed]:opacity-0 data-[closed]:translate-y-1"
          >
            <CloseOnOutsideScroll panelRef={panelRef} />
            <div className="flex items-start gap-3 px-4 py-3 border-b border-primary-subtle">
              <div className="w-9 h-9 bg-primary-subtle rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0">
                {getInitials(log.loggedByAppUserFullName)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">
                  <span className="font-medium text-white">{log.loggedByAppUserFullName}</span>
                  <span className="text-neutral-medium"> · </span>
                  <span className="font-medium text-[#7C6CF7]">{log.timeSpent}</span>
                </div>
                <div className="text-xs text-neutral-medium">Work date: {formatDate(log.dateStarted)}</div>
              </div>
              <CloseButton
                className="p-1 rounded-md text-neutral-medium hover:text-white transition-colors"
                title="Close"
              >
                <X className="w-4 h-4" />
              </CloseButton>
            </div>
            <p className="px-4 py-3 max-h-60 overflow-y-auto custom-scrollbar text-sm text-neutral-light whitespace-pre-wrap break-words">
              {description}
            </p>
          </PopoverPanel>
        </Popover>
      )}
    </>
  );
};

const formatLoggedOn = (createdAt: string) => {
  const date = new Date(createdAt);
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
};

interface WorkLogProps {
  projectId: string;
  taskId: string;
  projectKey: string;
  workItemKey: string;
  highlightUserUuid?: string;
}

const WorkLog: React.FC<WorkLogProps> = ({ projectId, taskId, projectKey, workItemKey, highlightUserUuid }) => {
  const { orgSlug } = usePmoNavigation();
  const { data: logs = [], isLoading } = useWorkLogs(projectId, taskId);
  
  const handleCopyLink = (timeEntryUuid: string) => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/${orgSlug}/pmo/${projectKey}/${workItemKey}#worklog-${timeEntryUuid}`;
    
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        toast.success('Link copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy link:', err);
        toast.error('Failed to copy link');
      });
  };
  const { mutate: deleteLog } = useDeleteWorkLog();
  const { mutate: updateLog } = useUpdateWorkLog();
  const authUser = useAppSelector(selectAuthUser);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<string | null>(null);
  const [highlightedLogId, setHighlightedLogId] = useState<string | null>(null);

  useEffect(() => {
    if (highlightUserUuid && logs.length > 0) {
      const targetLog = (logs as any[]).find(log => log.loggedByAppUserUuid === highlightUserUuid);
      if (targetLog) {
        setHighlightedLogId(targetLog.timeEntryUuid);
        const el = document.getElementById(`worklog-${targetLog.timeEntryUuid}`);
        if (el) {
          // Delay scrolling slightly to allow layout to settle
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
        
        // Remove highlight after 3 seconds
        const timer = setTimeout(() => {
          setHighlightedLogId(null);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [logs, highlightUserUuid]);

  const handleDeleteClick = (logId: string) => {
    setLogToDelete(logId);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (logToDelete) {
      deleteLog({ 
        projectUuid: projectId, 
        workItemUuid: taskId, 
        timeEntryUuid: logToDelete 
      });
      setDeleteModalOpen(false);
      setLogToDelete(null);
    }
  };

  const handleEditClick = (logId: string) => {
    setSelectedLogId(logId);
    setEditModalOpen(true);
  };

  const selectedLog = (logs as any[]).find((l) => l.timeEntryUuid === selectedLogId);
  const initialEditData: LogTimeData | null = selectedLog ? {
    dateStarted: selectedLog.dateStarted,
    timeSpentStr: selectedLog.timeSpent,
    timeSpent: selectedLog.minutesSpent,
    description: selectedLog.description,
    timeEntryUuid: selectedLog.timeEntryUuid,
  } : null;

  const { mutate: createLog } = useCreateWorkLog();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [view, setView] = useState<WorkLogView>(readStoredView);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(readStoredColumnSizing);
  // Bounds for the description popover, so it stays within the work log area
  const containerRef = useRef<HTMLDivElement>(null);

  // Lives here rather than in the table view so widths survive List/Table switches
  const table = useReactTable({
    data: logs as any[],
    columns: TABLE_COLUMNS,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
  });
  const isResizingColumn = !!table.getState().columnSizingInfo.isResizingColumn;

  // Persist once a drag ends rather than on every pointer move
  useEffect(() => {
    if (isResizingColumn) return;
    try {
      localStorage.setItem(COLUMN_SIZING_STORAGE_KEY, JSON.stringify(columnSizing));
    } catch {
      // Storage unavailable; widths just won't persist
    }
  }, [columnSizing, isResizingColumn]);

  const handleViewChange = (nextView: WorkLogView) => {
    setView(nextView);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, nextView);
    } catch {
      // Storage unavailable; the choice just won't persist
    }
  };

  // Filter only current user's logs
  const userLogs = (logs as any[]).filter(log => log.loggedByAppUserUuid === authUser?.appUserUuid);

  // Total time across all users for this task
  const totalMinutes = (logs as any[]).reduce((acc: number, log: any) => acc + (log.minutesSpent || 0), 0);

  const addLogModal = (
    <LogTimeModal
      isOpen={addModalOpen}
      existingUserLogs={userLogs}
      onClose={() => setAddModalOpen(false)}
      onSave={(data) => {
        createLog({
          projectUuid: projectId,
          workItemUuid: taskId,
          dateStarted: data.dateStarted,
          timeSpent: data.timeSpentStr,
          description: data.description,
        });
        setAddModalOpen(false);
      }}
    />
  );

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7C6CF7]"></div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full py-20 animate-fade-in">
        <div className="mb-6 opacity-80">
          <img 
            src="/assets/empty-work-log.svg" 
            alt="No work logs" 
            className="w-32 h-32 object-contain"
          />
        </div>
        <h3 className="text-neutral-medium text-base mb-4 font-medium">No time logged yet</h3>
        <button
          onClick={() => setAddModalOpen(true)}
          className="text-[#7C6CF7] hover:text-purple-300 transition-colors text-base font-medium flex items-center gap-1.5 active:scale-95 transform"
        >
          <span className="text-xl">+</span> Log Time
        </button>

        {addLogModal}
      </div>
    );
  }

  const viewToggleClass = (target: WorkLogView) =>
    `flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
      view === target ? 'bg-primary-subtle text-white' : 'text-neutral-medium hover:text-neutral'
    }`;

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-3 w-full">
      <div className="text-sm text-neutral-medium">
        Total logged <span className="ml-1 font-medium text-[#9B8EF0]">{formatTimeSpent(totalMinutes)}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 p-1 rounded-lg border border-primary-subtle">
          <button onClick={() => handleViewChange('list')} className={viewToggleClass('list')}>
            <List className="w-3.5 h-3.5" /> List
          </button>
          <button onClick={() => handleViewChange('table')} className={viewToggleClass('table')}>
            <Table className="w-3.5 h-3.5" /> Table
          </button>
        </div>
        <button
          onClick={() => setAddModalOpen(true)}
          className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] hover:opacity-90 text-white text-xs font-medium transition-all active:scale-95"
        >
          + Log Time
        </button>
      </div>
    </div>
  );

  const tableView = (
    <div className="w-full overflow-x-auto rounded-lg border border-primary-subtle">
      <table
        className={`table-fixed text-left text-sm ${isResizingColumn ? 'select-none cursor-col-resize' : ''}`}
        style={{ width: '100%', minWidth: table.getTotalSize() }}
      >
        {/* Fixed layout sizes every row from these, keeping headers and cells aligned while resizing.
            The non-resizable column gets no width so it takes the spare space; minWidth above still reserves its size. */}
        <colgroup>
          {table.getVisibleLeafColumns().map((column) => (
            <col key={column.id} style={column.getCanResize() ? { width: column.getSize() } : undefined} />
          ))}
        </colgroup>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="text-xs text-neutral-medium border-b border-primary-subtle">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="relative px-3 py-3 font-medium whitespace-nowrap overflow-hidden text-ellipsis border-r border-primary-subtle last:border-r-0 group"
                >
                  {header.column.columnDef.header as string}
                  {header.column.getCanResize() && (
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      onDoubleClick={() => header.column.resetSize()}
                      title="Drag to resize, double-click to reset"
                      className={`absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none hover:bg-purple-500/50 group-hover:bg-purple-500/50 ${
                        header.column.getIsResizing() ? 'bg-purple-500' : 'bg-transparent'
                      }`}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {(logs as any[]).map((log) => {
            const canEdit = log.loggedByAppUserUuid === authUser?.appUserUuid;
            const isHighlighted = highlightedLogId === log.timeEntryUuid;

            return (
              <tr
                key={log.timeEntryUuid}
                id={`worklog-${log.timeEntryUuid}`}
                className={`border-b border-primary-subtle last:border-b-0 align-middle transition-colors duration-700 ${isHighlighted ? 'bg-[#7C6CF7]/20' : ''}`}
              >
                <td className="px-3 py-3 border-r border-primary-subtle overflow-hidden">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 bg-primary-subtle rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0">
                      {getInitials(log.loggedByAppUserFullName)}
                    </div>
                    <span className="text-white truncate" title={log.loggedByAppUserFullName}>
                      {log.loggedByAppUserFullName}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3 text-white truncate border-r border-primary-subtle">
                  {formatDate(log.dateStarted)}
                </td>
                <td className="px-3 py-3 text-[#7C6CF7] font-medium truncate border-r border-primary-subtle">
                  {log.timeSpent}
                </td>
                <td className="px-3 py-3 text-neutral-light border-r border-primary-subtle overflow-hidden">
                  {log.description ? (
                    <DescriptionCell log={log} boundaryRef={containerRef} />
                  ) : (
                    <span className="text-neutral-medium">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-white truncate border-r border-primary-subtle" title={formatLoggedOn(log.createdAt)}>
                  {formatLoggedOn(log.createdAt)}
                </td>
                <td className="px-3 py-3 overflow-hidden">
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditClick(log.timeEntryUuid)}
                        className="p-1.5 rounded-md border border-primary-subtle text-neutral-medium hover:text-[#7C6CF7] transition-colors"
                        title="Edit"
                      >
                        <SquarePen className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(log.timeEntryUuid)}
                        className="p-1.5 rounded-md border border-primary-subtle text-neutral-medium hover:text-[#f25b5b] transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div ref={containerRef} className="space-y-4 pt-4 pb-10 flex flex-col items-start w-full">
      {toolbar}

      {view === 'table' ? tableView : (logs as any[]).map((log) => {
        const canEdit = log.loggedByAppUserUuid === authUser?.appUserUuid;
        const isHighlighted = highlightedLogId === log.timeEntryUuid;

        return (
          <div
            key={log.timeEntryUuid}
            id={`worklog-${log.timeEntryUuid}`}
            className={`flex gap-4 w-full group transition-all duration-700 p-2 -m-2 rounded-md ${isHighlighted ? 'bg-[#7C6CF7]/20 border border-[#7C6CF7]/50' : 'bg-transparent border border-transparent'}`}
          >
            <div className="w-10 h-10 bg-primary-subtle rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
              {getInitials(log.loggedByAppUserFullName)}
            </div>
            <div className="flex-1 w-full">
              <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
                <span className="font-medium text-white text-sm">{log.loggedByAppUserFullName}</span>
                <span className="text-neutral-medium text-sm">logged</span>
                <span className="text-[#7C6CF7] font-medium text-sm">{log.timeSpent}</span>
                {log.isEdited && (
                  <span className="text-[10px] bg-primary-subtle text-neutral-medium px-2 py-0.5 rounded-full ml-2">
                    Edited
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-neutral-medium text-xs mb-1">
                Work date: {formatDate(log.dateStarted)} • Logged on {formatLoggedOn(log.createdAt)}
                <button
                  onClick={() => handleCopyLink(log.timeEntryUuid)}
                  className="ml-1 p-1 rounded-md text-neutral-medium hover:text-blue-400 transition-colors"
                  title="Copy Link"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
              {log.description && (
                <p className="text-neutral-light text-sm mb-1.5 whitespace-pre-wrap max-w-full overflow-hidden break-words">{log.description}</p>
              )}
              {canEdit && (
                <div className="flex items-center gap-3 text-xs">
                  <button
                    onClick={() => handleEditClick(log.timeEntryUuid)}
                    className="text-[#7C6CF7] hover:text-purple-300 transition-colors"
                  >
                    Edit
                  </button>
                  <span className="text-neutral-dark/40">•</span>
                  <button
                    onClick={() => handleDeleteClick(log.timeEntryUuid)}
                    className="text-[#f25b5b] hover:text-red-400 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {addLogModal}

      <LogTimeModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        initialData={initialEditData}
        existingUserLogs={userLogs}
        onSave={(data) => {
          if (selectedLogId) {
            updateLog({
              timeEntryUuid: selectedLogId,
              projectUuid: projectId,
              workItemUuid: taskId,
              dateStarted: data.dateStarted,
              timeSpent: data.timeSpentStr,
              description: data.description,
            });
          }
          setEditModalOpen(false);
        }}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setLogToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Work Log?"
        message="Are you sure you want to delete this time entry? This action cannot be undone."
      />
    </div>
  );
};

export default WorkLog;
